import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { useState } from "react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Button } from "../../components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../../components/ui/card";
import { Field, FieldDescription, FieldLabel } from "../../components/ui/field";
import { Input } from "../../components/ui/input";

/** Staff form: set the copier/scanner sender address that gates notice filing. */
export default function FirmNoticeFilingCard({
	tenantId,
	scannerSenderAddress,
}: {
	tenantId: Id<"tenants">;
	scannerSenderAddress?: string;
}) {
	const setConfig = useMutation(api.staff.setFirmNoticeFilingConfig);
	const [value, setValue] = useState(scannerSenderAddress ?? "");
	const [saved, setSaved] = useState(false);

	const { mutate, isPending, error } = useReactQueryMutation({
		mutationFn: async () => {
			const trimmed = value.trim();
			await setConfig({
				tenantId,
				scannerSenderAddress: trimmed ? trimmed : undefined,
			});
		},
		onSuccess: () => setSaved(true),
	});

	const normalized = (scannerSenderAddress ?? "").toLowerCase();
	const dirty = value.trim().toLowerCase() !== normalized;

	return (
		<Card>
			<CardHeader>
				<CardTitle>Notice filing</CardTitle>
			</CardHeader>
			<CardContent>
				<form
					className="flex flex-col gap-3"
					onSubmit={(e) => {
						e.preventDefault();
						mutate();
					}}
				>
					<Field>
						<FieldLabel htmlFor="scanner-sender">
							Scanner sender address
						</FieldLabel>
						<Input
							id="scanner-sender"
							type="email"
							value={value}
							onChange={(e) => {
								setValue(e.target.value);
								setSaved(false);
							}}
							placeholder="copier@firm.com"
						/>
						<FieldDescription>
							Email address the office copier sends scans from. When set, the
							"file scanned notices" suggestion appears for emails from this
							sender. Leave blank to disable. Stored lowercased.
						</FieldDescription>
					</Field>
					{error ? (
						<p className="text-sm text-destructive">
							{error instanceof Error ? error.message : String(error)}
						</p>
					) : null}
					<div className="flex items-center gap-3">
						<Button type="submit" disabled={isPending || !dirty}>
							{isPending ? "Saving..." : "Save"}
						</Button>
						{saved && !dirty ? (
							<span className="text-sm text-muted-foreground">Saved</span>
						) : null}
					</div>
				</form>
			</CardContent>
		</Card>
	);
}
